/**
 * Panel principal configurable: el usuario elige qué widgets ver, dónde van y qué tamaño tienen.
 * La disposición se guarda por usuario (localStorage, ver Dashboard.Logic.ts).
 */

export type WidgetKind =
  | 'settlementsSummary'
  | 'pendingSettlements'
  | 'pinnedContracts'
  | 'expiringContracts'
  | 'importJobs'
  | 'quickLinks'
  | 'activeProtocols';

export const WIDGET_KINDS: WidgetKind[] = [
  'settlementsSummary',
  'pendingSettlements',
  'pinnedContracts',
  'expiringContracts',
  'importJobs',
  'quickLinks',
  'activeProtocols',
];

export const isWidgetKind = (value: unknown): value is WidgetKind =>
  typeof value === 'string' && (WIDGET_KINDS as string[]).includes(value);

/** Configuración propia de cada widget (sólo los que la tienen). */
export interface PinnedContractsSettings {
  contractIds: number[];
}

export type ExpiringContractsWindow = 30 | 60 | 90;

export interface ExpiringContractsSettings {
  days: ExpiringContractsWindow;
}

export interface QuickLinksSettings {
  /** Claves de `SideMenuItems` (contracts, settlements, ...). */
  keys: string[];
}

export type WidgetSettings = Record<string, unknown>;

export interface WidgetInstance {
  /** Único dentro del panel; es el `i` del layout. */
  id: string;
  kind: WidgetKind;
  settings?: WidgetSettings;
}

/** Posición y tamaño en la grilla de 12 columnas (unidades de grilla, no píxeles). */
export interface DashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface DashboardConfig {
  version: number;
  widgets: WidgetInstance[];
  layout: DashboardLayoutItem[];
}

/** Tamaños por tipo de widget, en unidades de grilla. */
export interface WidgetSizeMeta {
  w: number;
  h: number;
  minW: number;
  minH: number;
  maxH?: number;
}

export const DASHBOARD_COLS = 12;
/** Alto de una fila en píxeles (el alto real de un widget es h · ROW_HEIGHT + (h − 1) · margen). */
export const DASHBOARD_ROW_HEIGHT = 48;
export const DASHBOARD_MARGIN: [number, number] = [16, 16];

export const WIDGET_SIZES: Record<WidgetKind, WidgetSizeMeta> = {
  settlementsSummary: { w: 12, h: 3, minW: 6, minH: 3, maxH: 4 },
  pendingSettlements: { w: 7, h: 7, minW: 4, minH: 4 },
  pinnedContracts: { w: 5, h: 7, minW: 3, minH: 4 },
  expiringContracts: { w: 6, h: 6, minW: 4, minH: 4 },
  importJobs: { w: 6, h: 6, minW: 4, minH: 4 },
  quickLinks: { w: 12, h: 3, minW: 4, minH: 2, maxH: 6 },
  activeProtocols: { w: 6, h: 6, minW: 4, minH: 4 },
};

/** Props que recibe cada widget desde el panel. */
export interface WidgetProps {
  widget: WidgetInstance;
  /** El panel está en modo "Personalizar". */
  editing: boolean;
  /** El panel pidió abrir la configuración del widget (engranaje del marco). */
  settingsOpen: boolean;
  onCloseSettings: () => void;
  onSettingsChange: (settings: WidgetSettings) => void;
}
